import { useState } from "react"

import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import type { PersonalSource, PersonalSourceView } from "@/api/client/personal-sources"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { PERSONAL_SOURCE_SETTINGS_PATH } from "@/features/auth/utils/personal-source-callback"

import {
  formatGraceDate,
  hasVisiblePersonalSources,
  PERSONAL_SOURCE_KEYS,
  personalSourceUiState,
} from "./personal-sources-utils"
import {
  useConnectPersonalSource,
  usePersonalSources,
  useWithdrawPersonalSource,
} from "../hooks/use-personal-sources"

function SourceRow({
  view,
  graceDays,
  busy,
  onConnect,
  onWithdraw,
}: {
  view: PersonalSourceView
  graceDays: number
  busy: boolean
  onConnect: (source: PersonalSource) => void
  onWithdraw: (source: PersonalSource) => void
}) {
  const { t, i18n } = useTranslation()
  const state = personalSourceUiState(view)
  const keys = PERSONAL_SOURCE_KEYS[view.source]
  const title = t(keys.title, { defaultValue: view.source })
  const graceDate = formatGraceDate(view.grace_ends_at, i18n.language)

  let badge: { variant: "success" | "warning" | "secondary" | "outline"; label: string }
  if (state === "connected") {
    badge = {
      variant: "success",
      label: t("personalSources.status.connected", { defaultValue: "Connected" }),
    }
  } else if (state === "reconnect_required") {
    badge = {
      variant: "warning",
      label: t("personalSources.status.reconnect", { defaultValue: "Needs reconnecting" }),
    }
  } else if (state === "withdrawn") {
    badge = {
      variant: "secondary",
      label: t("personalSources.status.withdrawn", { defaultValue: "Withdrawn" }),
    }
  } else {
    badge = {
      variant: "outline",
      label: t("personalSources.status.off", { defaultValue: "Not connected" }),
    }
  }

  const canConnect = view.eligible && (state === "not_connected" || state === "withdrawn")

  return (
    <li
      className="flex flex-col gap-3 py-4 sm:flex-row sm:items-start sm:justify-between"
      data-source={view.source}
      data-state={state}
      data-testid={`personal-source-${view.source}`}
    >
      <div className="max-w-prose space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-medium">{title}</h3>
          <Badge variant={badge.variant}>{badge.label}</Badge>
        </div>
        <p className="text-muted-foreground text-sm">{t(keys.description, { defaultValue: "" })}</p>

        {state === "reconnect_required" ? (
          <p className="text-sm" role="status">
            {t("personalSources.reconnectHelp", {
              defaultValue:
                "The Microsoft connection stopped working. What was already collected stays searchable until you reconnect.",
            })}
          </p>
        ) : null}

        {state === "withdrawn" ? (
          <p className="text-sm" role="status">
            {graceDate
              ? t("personalSources.withdrawnHelp", {
                  date: graceDate,
                  defaultValue:
                    "Hidden now. Permanently deleted on {{date}}. Connect again before then to get it back.",
                })
              : t("personalSources.withdrawnHelpNoDate", {
                  days: graceDays,
                  defaultValue:
                    "Hidden now. Permanently deleted {{days}} days after withdrawal. Connect again before then to get it back.",
                })}
          </p>
        ) : null}

        {view.filth_ack === "pending" ? (
          <p className="text-muted-foreground text-xs" role="status">
            {t("personalSources.ackPending", {
              defaultValue: "Waiting for the search service to confirm this change.",
            })}
          </p>
        ) : null}

        {view.enabled && view.eligible && state === "not_connected" ? (
          <p className="text-muted-foreground text-xs">
            {t("personalSources.window", {
              days: view.history_window_days,
              defaultValue: "Only the last {{days}} days are collected.",
            })}
          </p>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-wrap gap-2">
        {canConnect ? (
          <Button
            data-testid={`personal-source-connect-${view.source}`}
            disabled={busy}
            size="sm"
            type="button"
            onClick={() => onConnect(view.source)}
          >
            {state === "withdrawn"
              ? t("personalSources.connectAgain", { defaultValue: "Connect again" })
              : t("personalSources.connect", { defaultValue: "Connect" })}
          </Button>
        ) : null}

        {state === "reconnect_required" ? (
          <Button
            data-testid={`personal-source-reconnect-${view.source}`}
            disabled={busy}
            size="sm"
            type="button"
            onClick={() => onConnect(view.source)}
          >
            {t("personalSources.reconnect", { defaultValue: "Reconnect" })}
          </Button>
        ) : null}

        {state === "connected" || state === "reconnect_required" ? (
          <Button
            data-testid={`personal-source-withdraw-${view.source}`}
            disabled={busy}
            size="sm"
            type="button"
            variant="outline"
            onClick={() => onWithdraw(view.source)}
          >
            {t("personalSources.withdraw", { defaultValue: "Withdraw" })}
          </Button>
        ) : null}
      </div>
    </li>
  )
}

/**
 * Settings: connect, reconnect and withdraw each personal source on its own.
 *
 * One Microsoft sign-in per source, each asking for one permission. Withdrawal
 * hides the content at once and deletes it after the grace period; nothing here
 * ever shows the content itself.
 */
export function PersonalSourcesPanel() {
  const { t, i18n } = useTranslation()
  const query = usePersonalSources()
  const connect = useConnectPersonalSource()
  const withdraw = useWithdrawPersonalSource()
  const [confirming, setConfirming] = useState<PersonalSource | null>(null)
  const [withdrawn, setWithdrawn] = useState<{ source: PersonalSource; date: string } | null>(null)

  if (query.isPending) {
    return (
      <p className="text-muted-foreground text-sm" role="status">
        {t("personalSources.loading", { defaultValue: "Checking your connections…" })}
      </p>
    )
  }
  if (query.isError) {
    return (
      <p className="text-sm" role="alert">
        {t("personalSources.unavailable", {
          defaultValue: "Personal sources could not be loaded right now.",
        })}
      </p>
    )
  }

  const { available, sources, grace_days: graceDays } = query.data
  const visible = sources.filter((view) => personalSourceUiState(view) !== "not_eligible")

  if (!available) {
    return (
      <p className="text-muted-foreground text-sm" data-testid="personal-sources-unavailable">
        {t("personalSources.notAvailable", {
          defaultValue: "Personal sources are not available on this deployment.",
        })}
      </p>
    )
  }
  if (!hasVisiblePersonalSources(sources)) {
    return (
      <p className="text-muted-foreground text-sm" data-testid="personal-sources-not-enabled">
        {t("personalSources.notEnabled", {
          defaultValue:
            "Your administrator has not turned personal sources on for you. Ask them if you would like to connect your own OneDrive, mail or Teams chats.",
        })}
      </p>
    )
  }

  const busy = connect.isPending || withdraw.isPending
  const confirmingView = visible.find((view) => view.source === confirming)

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground max-w-prose text-sm">
        {t("personalSources.intro", {
          defaultValue:
            "Connect your own OneDrive, mail or Teams chats so Hello AI can search them for you. What you connect is private to you: nobody else can see it, and it is only used in your own chats. You approve each source separately and can withdraw at any time.",
        })}
      </p>

      <ul className="divide-border divide-y" data-testid="personal-sources-list">
        {visible.map((view) => (
          <SourceRow
            key={view.source}
            busy={busy}
            graceDays={graceDays}
            view={view}
            onConnect={(source) => connect.mutate(source)}
            onWithdraw={(source) => setConfirming(source)}
          />
        ))}
      </ul>

      {connect.isError ? (
        <p className="text-sm" role="alert">
          {t("personalSources.connectFailed", {
            defaultValue:
              "The connection could not be started. Try again or contact your administrator.",
          })}
        </p>
      ) : null}
      {withdraw.isError ? (
        <p className="text-sm" role="alert">
          {t("personalSources.withdrawFailed", {
            defaultValue: "The withdrawal could not be completed. Please try again.",
          })}
        </p>
      ) : null}
      {withdrawn ? (
        <p className="text-sm" role="status">
          {t("personalSources.withdrawDone", {
            date: withdrawn.date,
            defaultValue:
              "Withdrawn. It is hidden now and deleted on {{date}}. You can also remove the app in My Apps to end the Microsoft permission.",
          })}
        </p>
      ) : null}

      <AlertDialog open={confirming !== null} onOpenChange={(open) => !open && setConfirming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("personalSources.confirmTitle", {
                source: confirmingView
                  ? t(PERSONAL_SOURCE_KEYS[confirmingView.source].title, {
                      defaultValue: confirmingView.source,
                    })
                  : "",
                defaultValue: "Withdraw {{source}}?",
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("personalSources.confirmBody", {
                days: graceDays,
                defaultValue:
                  "It is hidden in Hello AI right away and permanently deleted after {{days}} days. If you connect again before then, it comes back.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("personalSources.confirmCancel", { defaultValue: "Keep connected" })}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="personal-source-withdraw-confirm"
              disabled={withdraw.isPending}
              onClick={(event) => {
                event.preventDefault()
                if (!confirming) return
                withdraw.mutate(confirming, {
                  onSuccess: (result) => {
                    setWithdrawn({
                      source: result.source,
                      date: formatGraceDate(result.grace_ends_at, i18n.language),
                    })
                    setConfirming(null)
                  },
                })
              }}
            >
              {t("personalSources.confirmAction", { defaultValue: "Withdraw" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

/**
 * Compact entry in the General settings tab: a link to the full page, shown only
 * when at least one source is offered to this user. Renders nothing otherwise, so
 * with the feature off the settings dialog is unchanged.
 */
export function PersonalSourcesEntry() {
  const { t } = useTranslation()
  const query = usePersonalSources()
  if (!query.isSuccess || !query.data.available || !hasVisiblePersonalSources(query.data.sources)) {
    return null
  }
  return (
    <Card className="border-muted/50 shadow-none" data-testid="personal-sources-entry">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium">
          {t("personalSources.title", { defaultValue: "Personal sources" })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-start justify-between gap-4">
          <p className="max-w-prose text-sm">
            {t("personalSources.entryDescription", {
              defaultValue:
                "Let Hello AI search your own OneDrive, mail and Teams chats. Private to you, and you choose each source.",
            })}
          </p>
          <Button asChild size="sm" variant="outline">
            <Link to={PERSONAL_SOURCE_SETTINGS_PATH}>
              {t("personalSources.manage", { defaultValue: "Manage" })}
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
